import React, { useState, useEffect, useRef } from "react";
import {
  ChevronLeft,
  ShieldCheck,
  ArrowRight,
  ArrowRepeat,
  Trash,
  Key,
  Eraser,
  ShieldLock,
  PencilSquare,
  ArrowCounterclockwise,
} from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import Utils from "../../utils";
import { decryptData } from "../../utils/crypto";
import {
  createCustomerStorePin,
  updateCustomerStorePin,
} from "../../services/customerServices";
import { notify } from "../../components/Common/Alerts";
import { useNavigate } from "react-router-dom";

const SetPinPage = ({
  selectedProductStore,
  setSelectedSProductStore,
  pinData,
  setShowCatalogue,
  setCartData,
  setPinModelOpen,
  updateModel,
  setUpdateModel,
}) => {
  const { t } = useTranslation();
  const [pin, setPin] = useState(["", "", "", ""]);
  const [oldPin, setOldPin] = useState(["", "", "", ""]);
  const [newPin, setNewPin] = useState(["", "", "", ""]);
  const [btnLoading, setBtnLoading] = useState(false);

  const inputRefs = useRef([]);
  const nativeInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (nativeInputRef.current) {
      nativeInputRef.current.focus();
    }
  }, []);

  const handleNativeInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // only digits
    const digits = value.slice(0, 4).split(""); // max 4 digits
    setPin((prev) => {
      const newPin = [...prev];
      digits.forEach((digit, idx) => (newPin[idx] = digit));
      return newPin;
    });
  };

  const handleInputChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value.substring(0, 1);
    setPin(newPin);

    if (value && index < 3) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const focusNative = () => {
    if (nativeInputRef.current) {
      nativeInputRef.current.focus();
    }
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   let data = {
  //     store_id: pinData.id,
  //     pin: pin.join(""),
  //   };

  //   try {
  //     const response = await createCustomerStorePin(data);
  //     const decrypted = decryptData(response.data.data);
  //     setSelectedSProductStore(pinData);
  //     setShowCatalogue(true);
  //     setPinModelOpen(false);

  //     notify.success(decrypted.message);
  //     if (selectedProductStore?.id !== pinData.id) {
  //       setCartData([]);
  //     }
  //   } catch (err) {
  //     notify.error(Utils.getErrorMessage(err));
  //   } finally {
  //     setBtnLoading(false);
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const enteredPin = pin.join("");

    if (!enteredPin || enteredPin.length !== 4) {
      notify.error("Please enter a 4-digit PIN");
      return;
    }

    if (pinData?.pin) {
      if (enteredPin === pinData.pin) {
        setSelectedSProductStore(pinData);
        setShowCatalogue(true);
        setPinModelOpen(false);
        // notify.success("PIN verified successfully!");
        if (selectedProductStore?.id !== pinData.id) {
          setCartData([]);
        }
      } else {
        notify.error("Incorrect PIN. Please try again.");
      }
      return;
    }

    let data = {
      store_id: pinData.id,
      pin: enteredPin,
    };

    try {
      setBtnLoading(true);
      const response = await createCustomerStorePin(data);
      const decrypted = decryptData(response.data.data);

      setSelectedSProductStore(pinData);
      setShowCatalogue(true);
      setPinModelOpen(false);
      notify.success(decrypted.message);

      // Clear cart if switching store
      if (selectedProductStore?.id !== pinData.id) {
        setCartData([]);
      }
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnLoading(false);
    }
  };

  const handlePinInputChange = (e, index, type) => {
    const val = e.target.value.replace(/\D/g, "");
    const pinArr = type === "old" ? [...oldPin] : [...newPin];

    pinArr[index] = val;
    type === "old" ? setOldPin(pinArr) : setNewPin(pinArr);

    if (val && index < 3) {
      const nextInput = document.querySelector(`#${type}-${index + 1}`);
      if (nextInput) nextInput.focus();
    } else if (!val && index > 0) {
      const prevInput = document.querySelector(`#${type}-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVaultPinUpdate = async () => {
    const oldPinValue = oldPin.join("");
    const newPinValue = newPin.join("");

    if (oldPinValue.length !== 4 || newPinValue.length !== 4) {
      notify.error("Please enter 4 digits for both old and new PIN");
      return;
    }

    if (pinData?.pin && oldPinValue !== pinData.pin) {
      notify.error("Old PIN is incorrect. Please try again.");
      return;
    }

    const payload = {
      store_id: pinData.id,
      old_pin: oldPinValue,
      new_pin: newPinValue,
    };

    try {
      setBtnLoading(true);
      const response = await updateCustomerStorePin(payload); // API call
      const decrypted = decryptData(response.data.data);

      notify.success(decrypted.message || "Vault PIN updated successfully!");

      setOldPin(["", "", "", ""]);
      setNewPin(["", "", "", ""]);

      setUpdateModel(false);
      setSelectedSProductStore(null);
      setShowCatalogue(false);
      setPinModelOpen(false);
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <>
      {updateModel ? (
        <div className="flex flex-col min-h-screen bg-[#FFFBF7] dark:bg-[#0a0f1a] font-sans">
          <div className="flex sticky top-0 z-30 bg-[#FDFCFB]/80 backdrop-blur-md dark:bg-[#0a0f1a]/80 px-4 py-4 border-b border-gray-100 dark:border-[#1d2636]">
            <div className="max-w-6xl mx-auto flex items-center gap-4 w-full">
              <button
                className="text-gray-400 p-2 hover:bg-gray-100 dark:hover:bg-white/5 rounded-xl transition-all"
                onClick={() => {
                  setUpdateModel(false);
                  setShowCatalogue(true);
                  setPinModelOpen(false);
                  setSelectedSProductStore(null);
                  setShowCatalogue(false);
                }}
              >
                <ChevronLeft size={20} />
              </button>
              <h1 className="text-lg font-black text-gray-800 dark:text-white uppercase tracking-tight">
                {t("update")}{" "}
                <span className="text-orange-500">{t("securityPIN")}</span>
              </h1>
            </div>
          </div>

          <main className="flex-1 overflow-y-auto p-6 flex flex-col items-center">
            <div className="max-w-md w-full space-y-8 mt-4">
              <div className="space-y-4">
                <div className="flex items-center gap-3 px-2">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500">
                    <ShieldLock size={16} />
                  </div>
                  <p className="text-xs font-black uppercase tracking-widest text-gray-400">
                    {t("step1")}: {t("verifyOldPin")}
                  </p>
                </div>

                <div className="bg-white dark:bg-[#161e2d] p-6 rounded-[2rem] border border-gray-100 dark:border-[#232d3f] shadow-sm">
                  <div className="bg-white dark:bg-[#161e2d] p-6 rounded-[2rem] border border-gray-100 dark:border-[#232d3f] shadow-sm">
                    <div className="flex gap-3 justify-center">
                      {oldPin.map((digit, i) => (
                        <input
                          key={`old-${i}`}
                          id={`old-${i}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          placeholder="•"
                          className="w-12 h-14 lg:w-14 lg:h-16 text-center text-2xl font-black bg-gray-50 dark:bg-[#0a0f1a] border-2 border-transparent focus:border-orange-500 rounded-xl outline-none transition-all dark:text-white placeholder:text-gray-200"
                          onChange={(e) => handlePinInputChange(e, i, "old")}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* STEP 2: ENTER NEW PIN */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 px-2">
                  <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600">
                    <PencilSquare size={16} />
                  </div>
                  <p className="text-xs font-black uppercase tracking-widest text-orange-600/70">
                    {t("step2")}: {t("createNewPin")}
                  </p>
                </div>

                <div className="bg-white dark:bg-[#161e2d] p-6 rounded-[2rem] border-2 border-orange-100 dark:border-orange-900/30 shadow-xl shadow-orange-200/40 dark:shadow-none relative">
                  <div className="bg-white dark:bg-[#161e2d] p-6 rounded-[2rem] border-2 border-orange-100 dark:border-orange-900/30 shadow-xl shadow-orange-200/40 dark:shadow-none relative">
                    <div className="flex gap-3 justify-center">
                      {newPin.map((digit, i) => (
                        <input
                          key={`new-${i}`}
                          id={`new-${i}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          placeholder="•"
                          className="w-12 h-14 lg:w-14 lg:h-16 text-center text-2xl font-black bg-orange-50/30 dark:bg-[#0a0f1a] border-2 border-transparent focus:border-orange-500 rounded-xl outline-none transition-all dark:text-white placeholder:text-orange-200"
                          onChange={(e) => handlePinInputChange(e, i, "new")}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-4 space-y-4">
                <button
                  type="button"
                  onClick={handleVaultPinUpdate}
                  className={`w-full py-4 rounded-2xl text-white font-black text-sm uppercase tracking-[0.2em] flex items-center justify-center gap-3 transition-all active:scale-[0.98] ${
                    btnLoading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-orange-500 hover:bg-orange-600"
                  }`}
                  disabled={btnLoading}
                >
                  <ArrowRepeat size={20} />
                  {btnLoading ? t("updating") : t("updateVaultPIN")}
                </button>
                <button
                  onClick={() => {
                    setOldPin(["", "", "", ""]);
                    setNewPin(["", "", "", ""]);
                  }}
                  className="w-full py-3 text-gray-400 hover:text-red-500 font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-colors"
                >
                  <ArrowCounterclockwise size={14} />
                  {t("resetFields")}
                </button>
              </div>

              {/* Security Note */}
              <div className="bg-orange-50/50 dark:bg-white/5 p-4 rounded-2xl border border-dashed border-orange-200 dark:border-gray-800">
                <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-relaxed text-center font-medium">
                  {t("updateingYoutPin")}
                </p>
              </div>
            </div>
          </main>
        </div>
      ) : (
        <div className="flex flex-col h-screen bg-[#FFFBF7] dark:bg-[#0a0f1a] overflow-hidden font-sans">
          <header className="bg-[#E67E22] p-4 flex items-center gap-3 shadow-md hidden md:flex shrink-0">
            <button
              onClick={() => {
                setPinModelOpen(false);
                setSelectedSProductStore(null);
                setShowCatalogue(false);
                setUpdateModel(false);
              }}
              className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-[#E67E22] hover:bg-gray-100 transition-colors"
            >
              <ChevronLeft size={24} strokeWidth={2.5} />
            </button>

            <div className="flex items-center gap-2">
              <ShieldCheck size={22} className="text-white" strokeWidth={2.5} />
              <h1 className="text-white font-bold text-lg tracking-tight">
                {t("securitySetup")}
              </h1>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto px-6 flex items-center justify-center py-10 lg:py-0">
            <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
              <div className="text-center lg:text-left space-y-6">
                <div className="inline-flex bg-gradient-to-br from-orange-400 to-orange-600 text-white rounded-3xl w-20 h-20 items-center justify-center shadow-2xl shadow-orange-500/20">
                  <Key size={40} />
                </div>

                <div className="space-y-4">
                  <h2 className="text-4xl lg:text-6xl font-black leading-[1.1] text-gray-900 dark:text-white tracking-tighter">
                    {t("secureYour")}
                    <br />
                    <span className="text-orange-500">{t("vault")}</span>
                  </h2>
                  <p className="text-base lg:text-xl text-gray-500 dark:text-gray-400 font-medium max-w-sm mx-auto lg:mx-0 leading-relaxed">
                    {pinData?.pin ? t("verifyYourPin") : t("createPin")}
                  </p>
                </div>

                {/* Desktop Helper Text */}
                <div className="hidden lg:block pt-8 border-t border-gray-100 dark:border-gray-800">
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    End-to-End Encrypted Access
                  </div>
                </div>
              </div>

              {/* RIGHT SIDE: PIN Entry Area */}
              <div className="w-full max-w-md mx-auto lg:max-w-none">
                <div className="bg-white dark:bg-[#161e2d] rounded-[3rem] p-10 lg:p-14 shadow-[0_32px_64px_-16px_rgba(230,126,34,0.15)] dark:shadow-none border border-gray-50 dark:border-[#232d3f] relative overflow-hidden">
                  {/* Native Input Logic (Kept as per your functionality) */}
                  <input
                    ref={nativeInputRef}
                    type="number"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    maxLength={4}
                    className="sr-only"
                    autoFocus
                    onChange={handleNativeInputChange}
                  />

                  {/* Visual PIN Dots */}
                  <div
                    className="flex items-center justify-center gap-5 mb-12 cursor-pointer group"
                    onClick={focusNative}
                  >
                    {pin.map((digit, index) => (
                      <div
                        key={index}
                        className={`w-4 h-4 rounded-full transition-all duration-300 ${
                          digit
                            ? "bg-orange-500 scale-125 shadow-[0_0_15px_rgba(230,126,34,0.6)]"
                            : "bg-gray-200 dark:bg-gray-700 group-hover:bg-gray-300"
                        }`}
                      />
                    ))}
                  </div>

                  {/* Main PIN Box Inputs */}
                  <div className="flex gap-4 justify-center mb-12">
                    {pin.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (inputRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={1}
                        value={digit}
                        placeholder="•"
                        onChange={(e) =>
                          handleInputChange(e.target.value, index)
                        }
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        className="w-16 h-20 lg:w-20 lg:h-24 text-center text-4xl font-black text-gray-900 dark:text-white placeholder:text-gray-100 dark:placeholder:text-gray-800 bg-gray-50 dark:bg-[#0a0f1a] border-2 border-gray-100 dark:border-[#232d3f] rounded-[1.5rem] focus:border-orange-500 focus:ring-[6px] focus:ring-orange-500/10 outline-none transition-all shadow-inner"
                      />
                    ))}
                  </div>

                  {/* Primary Action Button */}
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="w-full py-5 rounded-2xl bg-[#E67E22] text-white font-black text-sm uppercase tracking-[0.25em] flex items-center justify-center gap-3 active:scale-[0.96] transition-all hover:bg-orange-600 shadow-2xl shadow-orange-500/30"
                  >
                    {pinData?.pin ? t("enterVault") : t("setNewPIN")}
                    <ArrowRight size={20} />
                  </button>

                  {/* Clear Action */}
                  <button
                    type="button"
                    className="w-full mt-8 text-center text-[11px] font-black uppercase tracking-widest text-gray-400 hover:text-orange-600 flex items-center justify-center gap-2 transition-colors"
                    onClick={() => setPin(["", "", "", ""])}
                  >
                    <Eraser size={16} /> {t("clearEntry")}
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      )}
    </>
  );
};

export default SetPinPage;
